// Unary tests — the grammar that goes inside a Decision Table input entry.
//
// Examples:
//   "Business"            → input = "Business"
//   >= 10                 → input >= 10
//   "Business", "Private" → input = "Business" OR input = "Private"
//   [1..10], (0..20)      → 1 ≤ input ≤ 10  OR  0 < input < 20
//   not(-1, 0)            → input ≠ -1 AND input ≠ 0
//   -                     → always matches
//
// We reuse the expression Parser for the endpoint-expression part. Top-level
// comma separation is done by scanning the source string (commas nested inside
// parens/brackets are ignored).

import { parse as parseExpr, FeelParseError } from './Parser';
import { evalAst } from './Evaluator';
import { tokenize, T } from './Lexer';
import { deepEqual, truthy } from './builtins';

export class FeelUnaryError extends Error {
  constructor(msg) { super(`[feel/unary] ${msg}`); }
}

// Split a unary-test source into top-level pieces separated by `,`.
function splitTopLevel(source) {
  const out = [];
  let depth = 0;
  let start = 0;
  let inStr = false;
  for (let i = 0; i < source.length; i++) {
    const c = source[i];
    if (inStr) {
      if (c === '\\') { i++; continue; }
      if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') { inStr = true; continue; }
    if (c === '(' || c === '[' || c === '{') depth++;
    else if (c === ')' || c === ']' || c === '}') depth--;
    else if (c === ',' && depth === 0) {
      out.push(source.slice(start, i).trim());
      start = i + 1;
    }
  }
  const last = source.slice(start).trim();
  if (last.length) out.push(last);
  return out;
}

// Parse a single positive test. Returns a function (value, context) => boolean.
function compilePositive(source) {
  const src = source.trim();
  if (!src) throw new FeelUnaryError('empty positive test');

  // "anything" marker inside a test list — only meaningful alone,
  // but we accept it for robustness.
  if (src === '-') return () => true;

  // Interval: starts with [ or ( or ]  (] is DMN's open-start notation)
  const first = src[0];
  const last  = src[src.length - 1];
  if ((first === '[' || first === '(' || first === ']') &&
      (last  === ']' || last  === ')' || last  === '[')) {
    const body = src.slice(1, -1);
    const sepIdx = findTopLevelDotDot(body);
    if (sepIdx >= 0) {
      const lowSrc  = body.slice(0, sepIdx).trim();
      const highSrc = body.slice(sepIdx + 2).trim();
      const lowAst  = parseExpr(lowSrc);
      const highAst = parseExpr(highSrc);
      const lowInclusive  = first === '[';
      const highInclusive = last  === ']';
      return (value, ctx) => {
        const low  = evalAst(lowAst, ctx);
        const high = evalAst(highAst, ctx);
        if (value == null) return false;
        const ge = lowInclusive  ? value >= low  : value > low;
        const le = highInclusive ? value <= high : value < high;
        return ge && le;
      };
    }
  }

  // Comparison operator prefix
  const cmpMatch = src.match(/^(<=|>=|!=|<|>|=)\s*(.*)$/s);
  if (cmpMatch) {
    const op = cmpMatch[1];
    const rhsAst = parseExpr(cmpMatch[2]);
    return (value, ctx) => {
      const r = evalAst(rhsAst, ctx);
      switch (op) {
        case '=':  return deepEqual(value, r);
        case '!=': return !deepEqual(value, r);
        case '<':  return value <  r;
        case '<=': return value <= r;
        case '>':  return value >  r;
        case '>=': return value >= r;
      }
      return false;
    };
  }

  // `not(...)` inside a positive test — unusual but valid in DMN.
  if (/^not\s*\(/.test(src)) {
    const inner = src.replace(/^not\s*\(/, '').replace(/\)\s*$/, '');
    const tests = splitTopLevel(inner).map(compilePositive);
    return (value, ctx) => !tests.some((t) => t(value, ctx));
  }

  // Bare expression → equality test (or boolean expression referring to `?`)
  const ast = parseExpr(src);
  return (value, ctx) => {
    // If the expression references `?` (the input placeholder), we must have
    // bound it in context already.
    const result = evalAst(ast, { ...ctx });
    // Booleans coming from predicate-like expressions apply directly.
    if (typeof result === 'boolean') return result;
    if (Array.isArray(result)) return result.some((x) => deepEqual(value, x));
    return deepEqual(value, result);
  };
}

function findTopLevelDotDot(source) {
  let depth = 0;
  let inStr = false;
  for (let i = 0; i < source.length - 1; i++) {
    const c = source[i];
    if (inStr) {
      if (c === '\\') { i++; continue; }
      if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') { inStr = true; continue; }
    if (c === '(' || c === '[' || c === '{') depth++;
    else if (c === ')' || c === ']' || c === '}') depth--;
    else if (c === '.' && source[i + 1] === '.' && depth === 0) return i;
  }
  return -1;
}

export function compileUnaryTests(source) {
  const trimmed = (source || '').trim();
  if (trimmed === '' || trimmed === '-') {
    return () => true;
  }
  if (/^not\s*\(/.test(trimmed) && /\)\s*$/.test(trimmed)) {
    const body = trimmed.replace(/^not\s*\(/, '').replace(/\)\s*$/, '');
    const tests = splitTopLevel(body).map(compilePositive);
    return (value, ctx = {}) => !tests.some((t) => t(value, ctx));
  }
  const tests = splitTopLevel(trimmed).map(compilePositive);
  return (value, ctx = {}) => tests.some((t) => t(value, ctx));
}

// Evaluate a unary test source string against a single input value. The
// optional context exposes named identifiers that may appear inside the
// test (e.g. `salary` referring to another input column).
export function evaluateUnaryTests(source, value, context = {}) {
  try {
    const fn = compileUnaryTests(source);
    return fn(value, context);
  } catch (err) {
    if (err instanceof FeelParseError) throw err;
    throw new FeelUnaryError(err.message || String(err));
  }
}

// Tokenize-only helper for callers that need it.
export { tokenize as _tokenize };
